// 🌐 React & State
import React, { createRef, useRef, useEffect, useState, RefObject } from "react";
import { useDrop } from "react-dnd";

// 🧠 State Management
import { observer } from "mobx-react-lite";
import pageBuilderStore from "../../stores/pageBuilderStore";
import blocksManager from "../../stores/blocks-manager";
import contentHistoryManager from "../../stores/content-history-manager";

// 🧩 Types
import {IContentBlock, IDesignBlock} from "../../types/iblock";
import {
  IButtonBlockData,
	IDividerBlockData,
  IHeroSliderBlockData,
  IColumnsBlockData,
  IHeadingBlockData,
  IHtmlBlockData,
  IImageBlockData,
  ISocialsBlockData,
  ITextBlockData,
  IVideoBlockData,
} from "../../types/content-block-data";
import { createBlockContent } from "../../utils/blocks-factory";


// 🧱 Block Components
import DesignBlock from "./shared/design-block";
import {
  ButtonBlock,
  HeroSliderBlock,
  ColumnsBlock,
  DividerBlock,
  HeadingBlock,
  HtmlBlock,
  ImageBlock,
  SocialsBlock,
  TextBlock,
  VideoBlock,
} from "./shared/content-blocks";
import DesignDropZone from "./shared/design-drop-zone";

// 🔧 Serialization
import serializationManager from "../../stores/serialization-manager";

// Utilities
import { debugPageContent } from "../../utils/debug";
import bodyStylesManager from "../../stores/body-styles-manager";
import contentManager from "../../stores/content-manager";
import glideBarManager from "../../stores/glidebar-manager";
import { IDimensionField } from "../../stores/state-interfaces";

const CONTENT_BLOCK_TYPES = new Set([
	"COLUMNS",
	"BUTTON",
	"DIVIDER",
	"HEADING",
	"TEXT",
	"IMAGE",
	"HERO_SLIDER",
	"VIDEO",
	"SOCIALS",
	"HTML"
]);

/**
 * DesignCanvas – Main Drag-and-Drop Editor Surface for PageBuilder
 *
 * @component
 * @description
 * The `DesignCanvas` serves as the central workspace where users build pages by dragging and
 * arranging blocks like Text, Headings, Images, Columns, etc. It synchronizes visual content,
 * state, and structural layout — forming the live-editable backbone of the PageBuilder.
 *
 * @features
 * - Accepts blocks from the Sidebar via react-dnd drop target
 * - Renders each block using the appropriate component (via `renderContentBlock`)
 * - Wraps every block in a `DesignBlock` to support editing, delete, and drag handle UI
 * - Allows reordering blocks via drag-and-drop, with live reindexing and animations
 * - Maintains synchronized `Map<string, RefObject>` for both design and content layers
 * - Automatically serializes block content to HTML and JSON after drop or reorder
 * - Shows empty state ("Drag items here") when no content blocks are present
 *
 * @state
 * @internal
 * - `currEditingBlockId` tracks which content block is in live editing mode
 * - `designBlockRefs` stores refs to each `DesignBlock` (outer wrapper div)
 * - `contentBlockRefs` stores refs to each block’s inner component (h1, div, etc.)
 *
 * @usage
 * <DesignCanvas onItemDropped={() => console.log("Dropped")} />
 *
 * @remarks
 * - Editing states (e.g. GlideBar position, in-place content updates) depend on live block refs
 * - DesignCanvas does not manage styles or themes — only structure and block arrangement
 * - Column support is planned; each cell will behave like a mini DesignCanvas
 *
 * @future
 * - Drop support inside ColumnBlock cells
 * - Block duplication and collapse toggles
 * - Section dividers, spacers, and advanced layout structures
 * - Real-time collaborative drag ghost and locking indicators
 *
 * @since 2025-06-07
 * @lastUpdated 2025-06-15
 *
 * @devLogs
 * - Integrated reorder logic via drag hover/drop with index re-sync
 * - Refactored internal ref maps from Record to Map to preserve order
 * - Added visual block highlighting and smooth transition animations
 * - Added `DesignBlock` wrapper with handlebar UI and custom drag interaction
 */
export default observer(function DesignCanvas() {
	/**
	 * Tracks the currently editing content block ID (e.g., for inline text editing).
	 *
	 * @remarks
	 * Used to show or hide contextual UIs (like rich text toolbars) tied to editing state.
	 * A value of `null` indicates that no block is being edited.
	 */
	const [currEditingBlockId, setCurrEditingBlockId] = useState<string | null>(null);

	/**
	 * We use this state to track specific DesignBlocks that have been hovered over by the user. 
	 * We use it for rendering the handlebars and focus highlight.
	 */
	const [hoveredBlockId, setHoveredBlockId] = useState<string | null>(null);

	/**
	 * ID of the DesignBlock currently being dragged by the user.
	 * Used to style the active drag target and to enable reordering logic during drop.
	 *
	 * @example
	 * if (currentlyDraggingId === block.designBlockId) { ... }
	 */
	const [currentlyDraggingId, setCurrentlyDraggingId] = useState<string | null>(null);

	/**
	 * ID of the DesignBlock currently being hovered over during a drag operation.
	 * A visual DropZone is rendered before this block to indicate potential insertion point.
	 *
	 * @example
	 * {hoveredTargetId === block.designBlockId && <DropZone />}
	 */
	const [hoveredTargetId, setHoveredTargetId] = useState<string | null>(null);
	
	const [{ isOver, canDrop }, drop] = useDrop(() => ({
		accept: [
			'DESIGN_BLOCKS',
			'CONTENT_BLOCKS',
		],
		drop: (item: {
			blockType: string;
			contentBlock?: IContentBlock; 
			contentBlockRef?: RefObject<HTMLElement>
			designBlockId?: string; 
			fromColIndex?: number;
			context: string; 
			otherColContentBlockId?: string;
		}, monitor) => {
			if (monitor.didDrop()) return; // 🛑 Prevent nested drops from triggering this

			console.log("Item dropped onto DesignCanvas:", item);

			// 🧱 Existing DESIGN_BLOCK dropped (either from ColumnCell, or at the same root-level)
			if (item.blockType === "DESIGN_BLOCKS") {
				if (item.context === "columns") {
					// 1. Locate the columns block and correct children
					const columnsDesignBlock = contentManager.getDesignContent().find(
						(blk: IDesignBlock) => blk.contentBlock.contentBlockId == item.otherColContentBlockId
					);
					if (!columnsDesignBlock) return { handled: false };

					const colContentBlock = columnsDesignBlock.contentBlock.blockData as IColumnsBlockData;
					const colChildren: IDesignBlock[] = colContentBlock.children[item.fromColIndex!];
					const fromIndex: number = colChildren.findIndex(
						(blk: IDesignBlock) => blocksManager.getDesignTree().get(blk.contentBlock.contentBlockId)?.designBlockId! === item.designBlockId
					);
					if (fromIndex === -1) return { handled: false };

					// 2. Remove the block from its column
					const [movingBlock] = colChildren.splice(fromIndex, 1);

					// 3. Generate new identity for canvas context
					const newDesignBlockId = crypto.randomUUID();
					const newDesignBlockRef = createRef<HTMLDivElement>();

					// 4. Insert at bottom of canvas
					contentManager.getDesignContent().push(movingBlock);

					// 5. Register in designTree
					blocksManager.addDesignTreeEntry({
						contentBlockId: movingBlock.contentBlock.contentBlockId,
						contentBlockType: movingBlock.contentBlock.blockType.toUpperCase(),
						designBlockId: newDesignBlockId,
						designBlockRef: newDesignBlockRef,
						contentBlockRef: blocksManager.getDesignTree().get(movingBlock.contentBlock.contentBlockId)?.contentBlockRef ?? createRef<HTMLElement>(),
						location: "canvas",
					});

					// 6. Reorder + re-serialize
					blocksManager.sortDesignTreeRefs();

					// 7. Take a peek at the designTree structure now.
					blocksManager.debugDesignTree();

					contentManager.setJsonContent(serializationManager.fromBlocksToJSON(contentManager.getDesignContent()));
					contentManager.setHtmlContent(serializationManager.fromBlocksToHTML(contentManager.getDesignContent()));
					
					blocksManager.setIsDraggingBlock(false);
					blocksManager.setHoveredDropZoneId(null);

					return { handled: true };
				}
				return;
			}

			// 🎯 For all CONTENT_BLOCK_TYPES (i.e. COLUMNS, BUTTONS, HEADING, TEXT, etc)
			if (CONTENT_BLOCK_TYPES.has(item.blockType)) {

				const newDesignBlockId = crypto.randomUUID();

				// 🆕 Create the refs
				const newDesignBlockRef = createRef<HTMLDivElement>();
				const newContentBlockRef = createRef<HTMLElement>(); // generic for now
				
				const newContentBlock: IContentBlock = createBlockContent(item.blockType);
				const newDesignBlock: IDesignBlock = {
					designBlockId: newDesignBlockId,
					designBlockRef: newDesignBlockRef,
					contentBlock: newContentBlock,
				};

				// 🧠 Register in designTree
				blocksManager.addDesignTreeEntry({
					contentBlockId: newDesignBlock.contentBlock.contentBlockId,
					designBlockId: newDesignBlockId,
					designBlockRef: newDesignBlockRef,
					contentBlockRef: newContentBlockRef,
					contentBlockType: item.blockType.toUpperCase(),
					location: "canvas",
				});

				// ➕ Add to canvas
				const currentBlocks = contentManager.getDesignContent();
				const newBlocks = [...currentBlocks, newDesignBlock]; // or use .concat(newDesignBlock)
				contentManager.setDesignContent(newBlocks);

				if (item.blockType.toLowerCase() === "heading" || item.blockType.toLowerCase() === "text") {
					setCurrEditingBlockId(newDesignBlock.contentBlock.contentBlockId);
					blocksManager.setBlockEditing(newDesignBlock.contentBlock.contentBlockId, item.blockType.toLowerCase());
					pageBuilderStore.setActivePanel('block-properties');
				}
				
				contentManager.debugDesignContent();
				
				contentManager.setJsonContent(serializationManager.fromBlocksToJSON(
				 	contentManager.getDesignContent()
				));

				blocksManager.setIsDraggingBlock(false);
				blocksManager.setHoveredDropZoneId(null);
			}
		},
		collect: (monitor) => ({
			
			isOver: monitor.isOver(),			// Is a DesignBlock, or Content Block over the DesignCanvas?
			canDrop: monitor.canDrop()		// Is this block accepted?
		})
	})); // for sidebar
	
	// Render different components based on type
	const renderContentBlock = (block: IContentBlock) => {
		let content: any;
		
		switch (block.blockType) {
			case 'columns': {
				content = block.blockData as IColumnsBlockData;
				
				const treeEntry = blocksManager.getDesignTree().get(block.contentBlockId);
				const ref = treeEntry?.contentBlockRef as RefObject<HTMLDivElement> ?? createRef<HTMLDivElement>();

				if (!treeEntry?.contentBlockRef) {
					blocksManager.updateDesignTreeEntry(block.contentBlockId, { contentBlockRef: ref });
				}

				return (
					<ColumnsBlock
						{...content}
						colContentBlockId={block.contentBlockId}
						contentBlockRef={ref}
						renderBlock={renderContentBlock}
						onClick={(e) => {
							e.stopPropagation(); // Prevent bubbling to canvas
							blocksManager.setBlockEditing(block.contentBlockId, block.blockType);
							pageBuilderStore.setActivePanel('block-properties');
						}}
						className="w-full"
					/>
				);
			}

			case 'button': {
				content = block.blockData as IButtonBlockData;
				
				const treeEntry = blocksManager.getDesignTree().get(block.contentBlockId);
				const ref = treeEntry?.contentBlockRef as RefObject<HTMLButtonElement> ?? createRef<HTMLButtonElement>();

				if (!treeEntry?.contentBlockRef) {
					blocksManager.updateDesignTreeEntry(block.contentBlockId, { contentBlockRef: ref });
				}

				return (
					<ButtonBlock
						{...content}
						contentBlockRef={ref}
						onClick={(e) => {
							e.stopPropagation(); // Prevent bubbling to canvas
							blocksManager.setBlockEditing(block.contentBlockId, block.blockType);
							pageBuilderStore.setActivePanel('block-properties');
						}}
					/>
				);
			}

			case 'divider': {
				content = block.blockData as IDividerBlockData;
				const treeEntry = blocksManager.getDesignTree().get(block.contentBlockId);
				const ref = treeEntry?.contentBlockRef as React.RefObject<HTMLHRElement> ?? React.createRef<HTMLHRElement>();

				if (!treeEntry?.contentBlockRef) {
					blocksManager.updateDesignTreeEntry(block.contentBlockId, { contentBlockRef: ref });
				}

				return (
					<div className="relative py-2 px-1 border border-dashed border-gray-300"
						onClick={(e) => {
							e.stopPropagation(); // Prevent bubbling to canvas
							blocksManager.setBlockEditing(block.contentBlockId, block.blockType);
							pageBuilderStore.setActivePanel('block-properties');
						}}
					>
						<DividerBlock 
							{...content} 
							contentBlockRef={ref}
						/>
					</div>
				);
			}

			case 'heading': {
				content = block.blockData as IHeadingBlockData;
				
				// Try to retrieve from designTree
				const treeEntry = blocksManager.getDesignTree().get(block.contentBlockId);

				// Use the existing ref or generate a new one
				const ref = treeEntry?.contentBlockRef as RefObject<HTMLHeadingElement>
					?? createRef<HTMLHeadingElement>();

				// If ref was missing, patch designTree with it
				if (!treeEntry?.contentBlockRef) {
					blocksManager.updateDesignTreeEntry(block.contentBlockId, {
						contentBlockRef: ref,
					});
				}

				return (
					<HeadingBlock
						{...content}
						contentBlockRef={ref}
						isEditing={currEditingBlockId === block.contentBlockId}
						onBlur={(e) => {
							if (e.relatedTarget && ref.current?.contains(e.relatedTarget as Node)) {
								return;
							}
							let rawHtml = e.currentTarget.innerHTML.trim();
							if (
							  !rawHtml ||
							  rawHtml === "" ||
							  rawHtml === "<br>" ||
							  rawHtml === '<span class="placeholder">Enter a Heading</span>' ||
							  rawHtml === "<span class='placeholder'>Enter a Heading</span>"
							) {
							  content.html = "";
								e.currentTarget.innerHTML = `<span className="placeholder">Enter a heading</span>`;
							} else {
							  content.html = rawHtml;
							}
							
							if (blocksManager.getBlockEditing().selectedBlockType !== "heading") {
								setTimeout(() => {
									if (!glideBarManager.getGlideBarState().isVisible) {
										setCurrEditingBlockId(null);
										blocksManager.setBlockEditing(undefined, undefined);
										pageBuilderStore.setActivePanel('content');
									}
								}, 1000);
							}
						}}
						onFocus={(e) => {
							const el = e.currentTarget;
						  // Check if we're showing the placeholder
						  if (
						    !el.innerText.trim() ||                   // Blank or whitespace
						    el.innerHTML.includes('placeholder')      // Has the placeholder class
						  ) {
						    // Remove placeholder so user can type
						    el.innerHTML = ""; // Now ready for input
						  }
							
							const selection = window.getSelection();
							if (selection && selection.rangeCount > 0) {
								// Save the range (clone, as it can change after blur)
								const caretRange = selection.getRangeAt(0).cloneRange();
								// Update our MobX glideBarState here.
								if (caretRange != null)
									glideBarManager.setCurrCaretPos(caretRange);
							}

							setCurrEditingBlockId(block.contentBlockId);
							blocksManager.setBlockEditing(block.contentBlockId, block.blockType.toLowerCase());
							pageBuilderStore.setActivePanel('block-properties');
						}}
						onClick={(e) => {
							e.stopPropagation();
							glideBarManager.setActiveBlock(block.contentBlockId, block.blockType.toLowerCase(), ref);
							glideBarManager.setIsEditingHeading(true);
						}}
					/>
				);
			}

			case 'text': {
				content = block.blockData as ITextBlockData;
				
				const treeEntry = blocksManager.getDesignTree().get(block.contentBlockId);
				const ref = treeEntry?.contentBlockRef as RefObject<HTMLDivElement> ?? createRef<HTMLDivElement>();

				if (!treeEntry?.contentBlockRef) {
					blocksManager.updateDesignTreeEntry(block.contentBlockId, { contentBlockRef: ref });
				}

				return (
					<TextBlock
						{...content}
						contentBlockRef={ref}
						isEditing={currEditingBlockId === block.contentBlockId}
						onFocus={(e) => {
							const el = e.currentTarget;
						  // Treat empty, placeholder, and <p>&nbsp;</p> as "blank"
						  if (
						    !el.innerText.trim() ||  // empty/whitespace only
						    el.innerHTML.includes('placeholder') ||
						    el.innerHTML.trim() === '<p>&nbsp;</p>'
						  ) {
						    el.innerHTML = "<p>&nbsp;</p>"; // Now ready for typing
								// We are only doing this if the TextBlock is still empty with a default value.
								const firstP = el.querySelector('p');
								if (firstP) {
									// Set caret inside the <p> at position 0
									const range = document.createRange();
									range.setStart(firstP, 0);
									range.collapse(true);
									const sel = window.getSelection();
									sel?.removeAllRanges();
									sel?.addRange(range);
								}
						  }
							
							const selection = window.getSelection();
							if (selection && selection.rangeCount > 0) {
								// Save the range (clone, as it can change after blur)
								const caretRange = selection.getRangeAt(0).cloneRange();
								// Update our MobX glideBarState here.
								if (caretRange != null)
									glideBarManager.setCurrCaretPos(caretRange);
							}
							
							setCurrEditingBlockId(block.contentBlockId);
							blocksManager.setBlockEditing(block.contentBlockId, block.blockType.toLowerCase());
							pageBuilderStore.setActivePanel('block-properties');
							// Temporarily shifting it here for testing.
							glideBarManager.setActiveBlock(block.contentBlockId, block.blockType.toLowerCase(), ref);
							glideBarManager.setIsEditingText(true);
						}}
						onBlur={(e) => {
							e.stopPropagation();
							if (e.relatedTarget && ref.current?.contains(e.relatedTarget as Node)) {
								return;
							}
							let rawHtml = e.currentTarget.innerHTML.trim();
							if (
							  !rawHtml ||
							  rawHtml === "" ||
							  rawHtml === "<br>" ||
							  rawHtml === "<p>&nbsp;</p>" ||
							  rawHtml === '<p class="placeholder">Enter your content here.</p>' ||
							  rawHtml === "<p class='placeholder'>Enter your content here.</p>"
							) {
							  content.html = "";
								e.currentTarget.innerHTML = `<p class="placeholder">Enter your content here.</p>`;
							} else {
							  content.html = rawHtml;
							}
							
							if (blocksManager.getBlockEditing().selectedBlockType !== "text") {
								setTimeout(() => {
									if (!glideBarManager.getGlideBarState().isVisible) {
										setCurrEditingBlockId(null);
										blocksManager.setBlockEditing(undefined, undefined);
										pageBuilderStore.setActivePanel('content');
									}
								}, 1000);
							}
						}}
					/>
				);
			}

			case 'image': {
				content = block.blockData as IImageBlockData;

				const treeEntry = blocksManager.getDesignTree().get(block.contentBlockId);
				const ref = treeEntry?.contentBlockRef as RefObject<HTMLImageElement> ?? createRef<HTMLImageElement>();

				if (!treeEntry?.contentBlockRef) {
					blocksManager.updateDesignTreeEntry(block.contentBlockId, { contentBlockRef: ref });
				}

				return (
					<ImageBlock 
						{...content} 
						contentBlockRef={ref}
						onClick={(e) => {
							e.stopPropagation();
							blocksManager.setBlockEditing(block.contentBlockId, block.blockType);
							pageBuilderStore.setActivePanel('block-properties');
						}}
						onImageUrlChange={newUrl => {
							content.src = newUrl;
						}}
					/>
				);
			}

			case 'hero_slider': {
				content = block.blockData as IHeroSliderBlockData;
				
				const treeEntry = blocksManager.getDesignTree().get(block.contentBlockId);
				const ref = treeEntry?.contentBlockRef as RefObject<HTMLDivElement> ?? createRef<HTMLDivElement>();

				if (!treeEntry?.contentBlockRef) {
					blocksManager.updateDesignTreeEntry(block.contentBlockId, { contentBlockRef: ref });
				}

				return (
					<HeroSliderBlock 
						{...content} 
						selectedIndex={blocksManager.getHeroSlideEditing().selectedSlideIndex}
						contentBlockRef={ref} 
						onClick={(e) => {
							e.stopPropagation();
							blocksManager.setBlockEditing(block.contentBlockId, block.blockType);
							blocksManager.setHeroSlideEditing({ selectedSlideIndex: 0, selectedSlide: content.slides[0] });
							pageBuilderStore.setActivePanel('block-properties');
						}}
					/>
				);
			}

			case 'video': {
				content = block.blockData as IVideoBlockData;

				const treeEntry = blocksManager.getDesignTree().get(block.contentBlockId);
				const ref = treeEntry?.contentBlockRef as RefObject<HTMLDivElement> ?? createRef<HTMLDivElement>();

				if (!treeEntry?.contentBlockRef) {
					blocksManager.updateDesignTreeEntry(block.contentBlockId, { contentBlockRef: ref });
				}

				return (
					<VideoBlock 
						{...content} 
						contentBlockRef={ref} 
						onClick={(e) => {
							e.stopPropagation();
							blocksManager.setBlockEditing(block.contentBlockId, block.blockType);
							pageBuilderStore.setActivePanel('block-properties');
						}}
					/>
				);
			}

			case 'socials': {
				content = block.blockData as ISocialsBlockData;

				const treeEntry = blocksManager.getDesignTree().get(block.contentBlockId);
				const ref = treeEntry?.contentBlockRef as RefObject<HTMLDivElement> ?? createRef<HTMLDivElement>();

				if (!treeEntry?.contentBlockRef) {
					blocksManager.updateDesignTreeEntry(block.contentBlockId, { contentBlockRef: ref });
				}

				return (
					<SocialsBlock 
						{...content} 
						blockRef={ref} 
						onClick={(e) => {
							e.stopPropagation();
							blocksManager.setBlockEditing(block.contentBlockId, block.blockType);
							pageBuilderStore.setActivePanel('block-properties');
						}}
					/>
				);
			}

			case 'html': {
				content = block.blockData as IHtmlBlockData;

				const treeEntry = blocksManager.getDesignTree().get(block.contentBlockId);
				const ref = treeEntry?.contentBlockRef as RefObject<HTMLDivElement> ?? createRef<HTMLDivElement>();

				if (!treeEntry?.contentBlockRef) {
					blocksManager.updateDesignTreeEntry(block.contentBlockId, { contentBlockRef: ref });
				}

				return (
					<HtmlBlock 
						{...content} 
						contentBlockRef={ref} 
						onClick={(e) => {
							e.stopPropagation();
							blocksManager.setBlockEditing(block.contentBlockId, block.blockType);
							pageBuilderStore.setActivePanel('block-properties');
						}}
					/>
				);
			}

			default:
				return (
					<div className="border border-red-300 text-red-500 p-2">
						Unknown block type: {block.blockType}
					</div>
				);
		}
	};

	/**
	 * Handles block drops within the `DesignCanvas`, supporting 3 major drop scenarios:
	 * 
	 * 1. 🆕 **Sidebar Block ➜ Canvas**
	 *    - A new `CONTENT_BLOCK` is dropped from the sidebar (e.g., "Text", "Image", etc.)
	 *    - A new block is created and inserted before the specified target block.
	 *
	 * 2. 🔄 **ColumnCell ➜ Canvas**
	 *    - A `DESIGN_BLOCK` originally from inside a ColumnCell is dropped onto a Canvas block.
	 *    - The block is moved from the column, reassigned a new designBlockId, and inserted into the canvas.
	 *
	 * 3. 🔁 **Canvas ➜ Canvas Reordering**
	 *    - Reorders a block within the canvas by inserting it before another block.
	 *
	 * After any drop, the `designTree` is re-sorted to match the updated visual layout,
	 * and the JSON/HTML outputs are regenerated.
	 *
	 * @param isFinal						Does the user want to insert the block at the end?
	 * @param blockType					The type of block being dropped (e.g. "TEXT", "IMAGE", or "DESIGN_BLOCKS")
	 * @param fromId						The `designBlockId` of the block being dragged
	 * @param toId							The `designBlockId` of the block being dropped onto (insertion target)
	 * @param colContentBlockId	Is this a drop from another ColumnsBlock?
	 * @param contentBlockId		(Optional) The `blockId` of the dragged content block, used for `designTree` lookup
	 * @param contentBlockRef 	(Optional) The DOM ref of the dragged block’s content, used for re-registration
	 */
	function handleDrop (
		isFinal: boolean, 
		blockType: string, 
		fromId: string, 
		toId: string, 
		colContentBlockId?: string, 
		contentBlockId?: string, 
		contentBlockRef?: RefObject<HTMLElement>
	) {

		// (1) Dropping Content Block from SidebarPanel
		if (CONTENT_BLOCK_TYPES.has(blockType)) {
			
			// We are dropping a new Content Block, so let's create a new block and insert where
			// the toId DesignBlock is.
			const newDesignBlockId = crypto.randomUUID();
			const newContentBlock: IContentBlock = createBlockContent(blockType);

			// Everything will be fresh and new.
			const newDesignBlockRef = createRef<HTMLDivElement>();
			const newContentBlockRef = createRef<HTMLElement>();
			
			// Create a new DesignBlock
			const newDesignBlock: IDesignBlock = {
				designBlockId: newDesignBlockId,
				designBlockRef: newDesignBlockRef,
				contentBlock: newContentBlock,
			};

			const currentBlocks = contentManager.getDesignContent();

			if (isFinal) {
				// Since it's the end DesignDropZone, we will push to the end of the array.
				currentBlocks.push(newDesignBlock);
			} else {
				// Where are we going to insert this new DesignBlock?
				const toIndex = currentBlocks.findIndex(
					(blk) => blocksManager.getDesignTree().get(blk.contentBlock.contentBlockId)?.designBlockId! === toId
				);
				if (toIndex === -1) return;

				currentBlocks.splice(toIndex, 0, newDesignBlock);
			}

			// Add an entry to the designTree
			blocksManager.addDesignTreeEntry({
				contentBlockId: newDesignBlock.contentBlock.contentBlockId,
				contentBlockType: newDesignBlock.contentBlock.blockType.toUpperCase(),
				designBlockId: newDesignBlockId,
				designBlockRef: newDesignBlockRef,
				contentBlockRef: newContentBlockRef,
				location: "canvas",
			});

			// Now we sort and sync the list
			blocksManager.sortDesignTreeRefs();
			blocksManager.debugDesignTree();

			// Save our content/data
			contentManager.setJsonContent(serializationManager.fromBlocksToJSON(currentBlocks));
			contentManager.setHtmlContent(serializationManager.fromBlocksToHTML(currentBlocks));

			// Now we reset all the block management related state values.
			blocksManager.setHoveredTarget(null);
			blocksManager.setDraggingFromColIndex(null);
			blocksManager.setIsDraggingBlock(false);
			blocksManager.setHoveredDropZoneId(null);

			return;
		} else {		// (2) Dropping a DesignBlock from somewhere in the `DesignCanvas` or `ColumnCell`
			console.log("About to move a Content Block:", contentBlockId);

			const treeEntry = contentBlockId
				? blocksManager.getDesignTree().get(contentBlockId)
				: undefined;

			if (treeEntry?.location === "columns") {
				// (2a) Dropping a DesignBlock from a ColumnCell ➜ DesignCanvas
				console.log("Dropping a DesignBlock [from ColumnCell] onto the DesignDropZone [to DesignCanvas]");

				// First, let's get the ColumnsBlock data & information by comparing the ColumnsBlock's 
				// contentBlockId with the colContentBlockId.
				const columnsBlock = contentManager.getDesignContent().find(
					(blk: IDesignBlock) => blk.contentBlock.contentBlockId == colContentBlockId
				);

				// Next, we extract the blockData from the ColumnsBlock.
				const colBlockData = columnsBlock?.contentBlock.blockData as IColumnsBlockData;

				// Then we get the list of children (an array of DesignBlock)
				const fromChildren = colBlockData.children[treeEntry.colIndex!];

				// Now, where in that list of children does this moving block belong to? Find it's index position.
				const srcIndex = fromChildren.findIndex(
					(blk) => blocksManager.getDesignTree().get(blk.contentBlock.contentBlockId)?.designBlockId! === fromId
				);
				if (srcIndex === -1) return;

				// Now, we can officially remove it from the array of DesignBlock within that column's cell
				const [movingBlock] = fromChildren.splice(srcIndex, 1);

				// 🎯 Assign new identity for canvas
				const newDesignBlockId = crypto.randomUUID();
				const newDesignBlockRef = createRef<HTMLDivElement>();

				// When performing a cross-cell or cross-canvas movement, we only keep the Content Block's data & 
				// information the same. The DesignBlock will always be assigned a new one.
				movingBlock.designBlockId = newDesignBlockId;
				movingBlock.designBlockRef = newDesignBlockRef;

				const currentBlocks = contentManager.getDesignContent();

				if (isFinal) {
					currentBlocks.push(movingBlock);
				} else {
					// Now, it is time for us to look at the final destination where we want to drop our moving block.
					const toIndex = currentBlocks.findIndex(
						(blk) => blocksManager.getDesignTree().get(blk.contentBlock.contentBlockId)?.designBlockId! === toId
					);
					if (toIndex === -1) return;

					// Found it? Amazing. Let's insert it at that index.
					currentBlocks.splice(toIndex, 0, movingBlock);
				}

				// Next, we want to also insert a new Entry into our `designTree`
				blocksManager.addDesignTreeEntry({
					contentBlockId: movingBlock.contentBlock.contentBlockId,
					contentBlockType: movingBlock.contentBlock.blockType.toUpperCase(),
					designBlockId: newDesignBlockId,
					designBlockRef: newDesignBlockRef,
					contentBlockRef: contentBlockRef ?? createRef<HTMLElement>(),
					location: "canvas",
				});

				// Now, we sort it. By sorting the designTree, we are also ensuring that it's synced to our 
				// designContent so there won't be any duplicate records.
				blocksManager.sortDesignTreeRefs();
				blocksManager.debugDesignTree();

				// Everytime a user finishes a move/reordering operation, we will save the final output
				contentManager.setJsonContent(serializationManager.fromBlocksToJSON(currentBlocks));
				contentManager.setHtmlContent(serializationManager.fromBlocksToHTML(currentBlocks));

				// Now we reset all the block management related state values.
				blocksManager.setHoveredTarget(null);
				blocksManager.setDraggingFromColIndex(null);
				blocksManager.setIsDraggingBlock(false);
				blocksManager.setHoveredDropZoneId(null);

				return;
			}

			// (2b) DesignCanvas-isolated Reordering operation.
			console.log("fromId:", fromId, ", toId:", toId);

			// It's always helpful during development to preview what the designTree looks like before we 
			// perform a reordering.
			blocksManager.debugDesignTree();

			const currentBlocks = contentManager.getDesignContent().slice();
			// We are we moving the DesignBlock from?
			const fromIndex = currentBlocks.findIndex(
				(blk: IDesignBlock) => blocksManager.getDesignTree().get(blk.contentBlock.contentBlockId)?.designBlockId! === fromId
			);
			// Where are we moving it to?
			const toIndex = currentBlocks.findIndex(
				(blk: IDesignBlock) => blocksManager.getDesignTree().get(blk.contentBlock.contentBlockId)?.designBlockId! === toId
			);

			if (fromIndex === -1) return;

			if (!isFinal && (toIndex === -1 || fromIndex === toIndex)) return;

			// Extract the current DesignBlock that we want to move.
			const [moved] = currentBlocks.splice(fromIndex, 1);

			if (isFinal) {
				currentBlocks.push(moved);
			} else {
				if (fromIndex < toIndex)
					currentBlocks.splice(toIndex-1, 0, moved);	// If the from location is smaller on the index range, insert at -1.
				else
					currentBlocks.splice(toIndex, 0, moved);		// If the from location is after the final location, insert at that index.
			}
			
			// Save everything as usual.
			contentManager.setDesignContent(currentBlocks); // Triggers re-render
			contentManager.setJsonContent(serializationManager.fromBlocksToJSON(currentBlocks)); // optional

			// Sort and sync our designTree
			blocksManager.sortDesignTreeRefs();
			blocksManager.debugDesignTree();

			// Now we reset all the block management related state values.
			blocksManager.setHoveredTarget(null);
			blocksManager.setDraggingFromColIndex(null);
			blocksManager.setHoveredDropZoneId(null);
		}

		setTimeout(() => blocksManager.setDropHandled(false), 200);
	}

	function deleteBlock(designBlockId: string) {
		const blockIndex = contentManager.getDesignContent().findIndex(
			(blk: IDesignBlock) => blocksManager.getDesignTree().get(blk.contentBlock.contentBlockId)?.designBlockId! === designBlockId
		);

		console.log("blockIndex:", blockIndex);

		if (blockIndex === -1) return;

		const [removedBlock] = contentManager.getDesignContent().splice(blockIndex, 1);

		if (pageBuilderStore.getIsEmail()) {
			pageBuilderStore.removeAddedFields(removedBlock.contentBlock.contentBlockId);
		}

		// ❌ Remove refs from global designTree
		blocksManager.removeDesignTreeEntry(removedBlock.contentBlock.contentBlockId);

		// Rebuild refs to maintain clean order
		blocksManager.sortDesignTreeRefs();

		// Serialize updated blockContent
		contentManager.setJsonContent(serializationManager.fromBlocksToJSON(contentManager.getDesignContent()));
		contentManager.setHtmlContent(serializationManager.fromBlocksToHTML(contentManager.getDesignContent()));

		// Notify user
		const name = removedBlock.contentBlock.blockType.charAt(0).toUpperCase() + removedBlock.contentBlock.blockType.slice(1);
		pageBuilderStore.setToast(`${name} block removed.`);
	}
	
	return (
		<div 
			className={`relative flex flex-col ${bodyStylesManager.getLayoutStyles()?.containerType === "full"
					? "w-full" : `w-[${bodyStylesManager.getLayoutStyles()?.maxWidth?.value}${bodyStylesManager.getLayoutStyles()?.maxWidth?.unit}] mx-auto`} overflow-y-auto h-full transition-colors ${
				isOver
						? "border-2 border-blue-400 bg-blue-50"
						: "border border-r border-l bg-white"
			}`}
			style={{
				padding: "20px",
				width: bodyStylesManager.getLayoutStyles()?.containerType === "boxed"
						? `${bodyStylesManager.getLayoutStyles()?.maxWidth?.value}${bodyStylesManager.getLayoutStyles()?.maxWidth?.unit}`
						: "100%",
			}}
		>
			<div
				ref={drop}
				className={`flex flex-col w-full bg-white z-10`}
				style={{
					width: "100%",
					position: "relative",
				}}

				onClick={(e) => {
					e.stopPropagation();
					console.log("Exit editing mode for any block");
					setCurrEditingBlockId(null);
					blocksManager.setBlockEditing(undefined, undefined);
					pageBuilderStore.setActivePanel('content');
				}}
			>
				{contentManager.getDesignContent().length === 0 ? (
					<div className="p-4 border border-dashed border-gray-300 bg-white w-full min-h-[710px] z-15">
						<span>Drag items here to start.</span>
					</div>
				) : (
					<div 
						className="flex flex-col p-4 border border-dashed border-gray-300 min-h-[710px] bg-white w-full z-15"
					>
						{contentManager.getDesignContent().map((block: IDesignBlock) => {
							const treeEntry = blocksManager
								.getDesignTree()
								.get(block.contentBlock.contentBlockId);

							return (
								<React.Fragment key={block.designBlockId}>
									<DesignDropZone
										isFinal={false}
										targetId={block.designBlockId}
										context={"canvas"}
										onDrop={(
											isFinal,
											blockType: string,
											fromId?: string,
											otherColContentBlockId?: string,
											contentBlockId?: string,
											contentBlockRef?: RefObject<HTMLElement>
										) =>
											handleDrop(
												isFinal,
												blockType,
												fromId ?? "",
												block.designBlockId,
												otherColContentBlockId ?? "",
												contentBlockId,
												contentBlockRef
											)
										}
									/>
									<DesignBlock
										designBlockId={block.designBlockId}
										designBlockRef={block.designBlockRef}
										blockContext={"canvas"}
										contentBlock={block.contentBlock}
										isEditing={
											currEditingBlockId === block.contentBlock.contentBlockId
										}
										isDragging={
											blocksManager.getDraggingBlockId() === block.designBlockId
										}
										onDelete={() => deleteBlock(block.designBlockId)}
										onDragStart={(id) => {
											blocksManager.setDraggingBlock(id);
											blocksManager.setFromDesignBlockId(id);
											blocksManager.setDraggingFromColIndex(-1);
										}}
										onDragEnd={() => {
											// Clear drag state
											blocksManager.setDraggingBlock(null);
											blocksManager.setHoveredTarget(null);
											blocksManager.setDraggingFromColIndex(null);

											// Reset editing state (optional)
											setCurrEditingBlockId(null);

											// Also clear GlideBar state, if applicable
											glideBarManager.setActiveBlock("", "", null);
											glideBarManager.setIsEditingHeading(false);
											glideBarManager.setIsEditingText(false);
										}}
										isHovered={hoveredBlockId === block.designBlockId}
										onHover={(id) => {
											if (
												blocksManager.getDraggingBlockId() &&
												blocksManager.getDraggingBlockId() !== id
											) {
												blocksManager.setHoveredTarget(id);
											}
										}}
										onMouseEnter={() => {
											setHoveredBlockId(block.designBlockId);

											blocksManager.debugDesignTree(
												"After dragging out from ColumnCell..."
											);
										}}
										onMouseLeave={() => setHoveredBlockId(null)}
									>
										{renderContentBlock(block.contentBlock)}
									</DesignBlock>
								</React.Fragment>
							);
						})}
						{contentManager.getDesignContent().length > 0 && (
							<DesignDropZone
								isFinal={true}
								context="canvas"
								onDrop={(
									isFinal,
									blockType: string,
									fromId?: string,
									otherColContentBlockId?: string,
									contentBlockId?: string,
									contentBlockRef?: RefObject<HTMLElement>
								) => handleDrop(
									isFinal, 
									blockType, 
									fromId ?? "", 
									"",
									otherColContentBlockId ?? "", 
									contentBlockId ?? "", 
									contentBlockRef ?? undefined
								)}
							/>
						)}
					</div>
				)}
			</div>
		</div>
  );
});
